import React from 'react';
import PropTypes from 'prop-types';

const money = (value) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(Number(value || 0));

export function providerDisplayName(option) {
  const raw = String((option && (option.name || option.providerKey)) || 'payment gateway').replace(/^provider\./i, '');
  return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : 'Payment gateway';
}

function ProviderBrand({ option }) {
  const short = String(option.providerKey || option.name || '?').split('.').pop() || '?';
  const logos = {
    paystack: '/images/payment/paystack.svg',
    flutterwave: '/images/payment/flutterwave-symbol.png',
    interswitch: '/images/payment/interswitch.svg',
    squad: '/images/payment/squad.ico',
  };
  const base = process.env.PUBLIC_URL || '';
  const logoPath = logos[short.toLowerCase()] || option.logo;
  const logo = logoPath ? base + logoPath : null;
  const [broken, setBroken] = React.useState(false);
  const colors = {
    paystack: '#09A5DB',
    flutterwave: '#F5A623',
    interswitch: '#00B4A6',
    squad: '#7B61FF',
  };
  const color = colors[short.toLowerCase()] || '#6c757d';
  if (!logo || broken) {
    const initial = short.charAt(0).toUpperCase();
    return (
      <div
        className="payment-option-brand d-inline-flex align-items-center justify-content-center mr-3 flex-shrink-0"
        style={{ width: 40, height: 40, borderRadius: '0.5rem', background: color, color: '#fff', fontWeight: 'bold', fontSize: '1rem' }}
        aria-hidden="true"
      >
        {initial}
      </div>
    );
  }
  return (
    <img
      className="payment-option-brand mr-3 flex-shrink-0"
      src={logo}
      alt={providerDisplayName(option)}
      onError={() => setBroken(true)}
      style={{ width: 40, height: 40, borderRadius: '0.5rem', objectFit: 'contain', padding: 4, background: '#fff', border: `1px solid ${color}` }}
    />
  );
}

export default function PaymentOptionsModal({
  title,
  description,
  loading,
  amountDue,
  payment,
  paymentOptions,
  selectedProvider,
  onSelectProvider,
  availableCredit,
  useCredit,
  creditAmount,
  creditLimit,
  onUseCreditChange,
  onCreditAmountChange,
  onSubmit,
  onClose,
  checkout,
  busy,
  submitting,
}) {
  const due = Number(amountDue || payment?.total_due || payment?.amount || 0);
  const hasCredit = Number(availableCredit) > 0;
  const applied = useCredit ? Math.min(Number(creditAmount) || 0, Number(creditLimit) || 0) : 0;
  const remaining = Math.max(0, due - applied);
  const selected = paymentOptions.find((option) => option.providerKey === selectedProvider);
  const selectedName = providerDisplayName(selected);

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true" aria-label={title || 'Payment options'}>
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" role="document">
          <div className="modal-content shadow">
            <div className="modal-header">
              <h5 className="modal-title">{title || 'Payment'}</h5>
              <button type="button" className="close" aria-label="Close" onClick={onClose} disabled={busy}>
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              {loading && <p className="mb-0">Preparing payment…</p>}
              {!loading && !payment && (
                <>
                  {description && <p className="mb-3">{description}</p>}
                  <div className="payment-total-amount text-center mb-4">
                    <div className="small text-muted text-uppercase font-weight-bold">Total Amount</div>
                    <div className="h3 font-weight-bold">{money(due)}</div>
                  </div>
                  {hasCredit && (
                    <>
                      <p className="text-muted mb-2">
                        Available Credit: <strong>{money(availableCredit)}</strong>
                      </p>
                      <label className="d-flex align-items-center mb-3">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={useCredit}
                          onChange={onUseCreditChange}
                          disabled={busy}
                          aria-label="Apply available credit"
                        />
                        Apply Credit
                      </label>
                      {useCredit && (
                        <input
                          aria-label="Credit amount"
                          type="number"
                          min="0.01"
                          step="0.01"
                          max={creditLimit}
                          className="form-control mb-3"
                          value={creditAmount}
                          onChange={onCreditAmountChange}
                          disabled={busy}
                        />
                      )}
                      {useCredit && (
                        <div className="border-top border-bottom py-2 mb-3">
                          <div className="d-flex justify-content-between">
                            <span>Balance</span>
                            <strong>{money(remaining)}</strong>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {remaining > 0 && (
                    <fieldset className="payment-options-fieldset mb-3">
                      <legend className="small text-uppercase font-weight-bold text-secondary mb-3">Choose Payment Gateway</legend>
                      <div className="payment-options-grid">
                        {paymentOptions.map((option) => {
                          const isSelected = selectedProvider === option.providerKey;
                          return (
                            <button
                              key={option.providerKey}
                              type="button"
                              className={`payment-option-card btn text-left d-flex align-items-center w-100 ${
                                isSelected ? 'active border-primary shadow-sm' : 'btn-light border'
                              }`}
                              onClick={() => onSelectProvider(option.providerKey)}
                              aria-pressed={isSelected}
                              disabled={busy}
                              aria-label={providerDisplayName(option)}
                            >
                              <ProviderBrand option={option} />
                              <span className="payment-option-text flex-grow-1 ml-2">
                                <span className="payment-option-name d-block font-weight-bold">{providerDisplayName(option)}</span>
                                <span className="payment-option-subtitle d-block small text-muted">Pay securely with {providerDisplayName(option)}</span>
                              </span>
                              <span className={`payment-option-radio ml-2 ${isSelected ? 'selected' : ''}`} aria-hidden="true" />
                            </button>
                          );
                        })}
                      </div>
                    </fieldset>
                  )}
                  <p className="payment-redirect-notice small text-muted text-center mb-3">
                    You will be redirected to the selected payment gateway to complete your payment securely.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary btn-block font-weight-bold"
                    onClick={onSubmit}
                    disabled={busy || (remaining > 0 && !selectedProvider)}
                  >
                    {submitting ? 'Starting…' : remaining > 0 ? `Continue to ${selectedName}` : 'Pay with Available Credit'}
                  </button>
                </>
              )}
              {!loading && payment && (
                <>
                  <p className="text-muted mb-3">
                    Balance: <strong>{money(payment.total_due || payment.amount)}</strong>
                  </p>
                  {checkout}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={busy ? undefined : onClose} role="presentation" />
    </>
  );
}

PaymentOptionsModal.propTypes = {
  title: PropTypes.string,
  description: PropTypes.node,
  loading: PropTypes.bool,
  amountDue: PropTypes.number,
  payment: PropTypes.object,
  paymentOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedProvider: PropTypes.string,
  onSelectProvider: PropTypes.func.isRequired,
  availableCredit: PropTypes.number,
  useCredit: PropTypes.bool,
  creditAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  creditLimit: PropTypes.number,
  onUseCreditChange: PropTypes.func.isRequired,
  onCreditAmountChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  checkout: PropTypes.node,
  busy: PropTypes.bool,
  submitting: PropTypes.bool,
};
